import React, { useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import {
  RadioGroup,
  Radio,
  TextField,
  FormControlLabel,
  FormControl,
  Chip,
} from "@material-ui/core";

import { Colors } from "../../../constants";
import { Cancel } from "@material-ui/icons";
import { agentTypeERP } from "../../../constants/order";
import { Autocomplete } from "@material-ui/lab";

const propTypes = {
  name: PropTypes.string.isRequired,
  typeList: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ),
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  handleOutput: PropTypes.func.isRequired,
  value: PropTypes.exact({
    type: PropTypes.any,
    term: PropTypes.string,
  }),
};
const defaultProps = {
  typeList: [],
  placeholder: "",
  value: {
    type: undefined,
    term: "",
  },
};

const FormControlField = styled(FormControl)`
  width: 100%;
`;

const ListItemLabel = styled(FormControlLabel)`
  padding-top: 4px;
  word-break: normal;
  padding-bottom: 4px;
`;

const SearchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 16px;
  margin: 0 16px;
`;

const SearchTypeWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 47px;
  gap: 8px;
`;

const SearchTypeLabel = styled.div`
  height: 20px;
  font-size: 16px;
  color: ${Colors.SECONDARY_MEDIUM};
  font-weight: bold;
  white-space: nowrap;
`;

const SearchTypeRadioGroup = styled.div`
  display: flex;
  align-items: center;
`;

const SearchFieldWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  margin-top: 16px;
`;

const FieldWrapper = styled.div`
  flex: 1;
`;

const ChipStyled = styled(Chip)`
  font-size: 13px;
  font-weight: 400;
  margin-right: 8px;
`;

const CancelStyled = styled(Cancel)`
  width: 24px;
  height: 24px;

  color: ${Colors.SECONDARY_MEDIUM};

  &:hover {
    color: ${Colors.SECONDARY_MEDIUM};
  }
`;

const FilterSearchForm = ({
  typeList,
  placeholder,
  name,
  value,
  type,
  tagBusinessUnityList,
  handleOutput,
  onChangeType,
  onChangeTerm,
  minChar,
  skip,
}) => {
  const [error, setError] = useState(false);

  const _getPlaceholder = () => {
    if (typeof placeholder === "function") {
      return placeholder(value);
    }

    return placeholder;
  };

  const _getType = () => {
    if (typeof type === "function") {
      return type(value);
    }

    return type;
  };

  const _onChangeTermName = (e) => {
    const termVal = e.target.value;

    if (error && minChar && termVal.length >= minChar) {
      setError(false);
    }

    onChangeTerm(e);
    _onSearch(e);
  };

  const _onChangeTermCategorie = (event, categoriesSelected) => {
    const categoriesListSelected = Object.fromEntries(
      categoriesSelected.map((item) => [item.id, true])
    );

    const newEvent = {
      ...event,
      target: {
        ...event.target,
        value: categoriesListSelected,
      },
    };

    onChangeTerm(newEvent);
    _onSearch(newEvent);
  };

  const _onSearch = (e) => {
    const termVal = e.target?.value ?? "";

    if (minChar && termVal.length < minChar) {
      setError(true);
    } else {
      setError(false);
      handleOutput(skip);
    }
  };

  return (
    <SearchWrapper>
      <RadioGroup
        aria-label={`${name}-type`}
        name={`${name}-type`}
        value={value.type || (typeList.length && typeList[0].value)}
        onChange={onChangeType}
      >
        <SearchTypeWrapper>
          <SearchTypeLabel>Buscar por:</SearchTypeLabel>

          {typeList.length ? (
            <SearchTypeRadioGroup>
              {typeList.map((typeItem) => (
                <ListItemLabel
                  key={`${name}-${typeItem.value}`}
                  value={typeItem.value}
                  control={<Radio color="primary" />}
                  label={typeItem.label}
                />
              ))}
            </SearchTypeRadioGroup>
          ) : null}
        </SearchTypeWrapper>
      </RadioGroup>

      <FormControlField component="fieldset">
        <SearchFieldWrapper>
          <FieldWrapper>
            {value.type === agentTypeERP.CATEGORIE ? (
              <Autocomplete
                multiple
                limitTags={2}
                options={tagBusinessUnityList?.list}
                getOptionLabel={(option) => option.name}
                onChange={(event, newValue) =>
                  _onChangeTermCategorie(event, newValue)
                }
                renderTags={(values, getTagProps) =>
                  values.map((option, index) => (
                    <ChipStyled
                      key={`tag-${option.name}-${option.id}`}
                      variant="default"
                      label={option.name}
                      deleteIcon={<CancelStyled />}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={_getPlaceholder()}
                    variant="outlined"
                  />
                )}
                ListboxProps={{
                  style: { minHeight: 100, overflow: "auto" },
                }}
              />
            ) : (
              <TextField
                label={_getPlaceholder()}
                name={name}
                value={value.term}
                onChange={(event) => _onChangeTermName(event)}
                onKeyDown={(e) => {
                  if(_getType() === 'number' && (e.key === 'e' || e.key === 'E')) {
                    e.preventDefault();
                  }
                  
                  if (e.key === "Enter") {
                    _onSearch(e);
                  }
                }}
                fullWidth
                error={error}
                variant="outlined"
                helperText={error ? `Min. ${minChar} caracteres` : ""}
                InputProps={{
                  type: _getType(),
                }}
              />
            )}
          </FieldWrapper>
        </SearchFieldWrapper>
      </FormControlField>
    </SearchWrapper>
  );
};

FilterSearchForm.propTypes = propTypes;
FilterSearchForm.defaultProps = defaultProps;

export default FilterSearchForm;
